import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { HsButtonModule, HsTabGroupModule } from '@hsetu/ngui-components';
import {
  ContactType,
  HsContact,
  HsContactModule,
  HsGenderType,
  Iso3166CountryType,
} from '@hsetu/ngui-views';
import { ContactMapper } from '@modules/contact/mappers/contact.mapper';
import { KfmContactBankAccountFormComponent } from '../contact-bank-account-form/kfm-contact-bank-account-form.component';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { KfmContactRegistrationsComponent } from '../contact-registrations/kfm-contact-registrations.component';
import { BankAccount } from '@modules/contact/models/bank-account.model';
import { Registration } from '@modules/contact/models/registration.model';
import { Contact } from '@modules/contact/models/contact.model';
import {
  ADDRESS_TAB_LABEL,
  BANK_TAB_LABEL,
  CANCEL_LABEL,
  ID_TAB_LABEL,
  SAVE_LABEL,
} from '@modules/contact/i18n/label-text-constants';

@Component({
  selector: 'kfm-contact-create-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HsTabGroupModule,
    HsContactModule,
    HsButtonModule,
    KfmContactBankAccountFormComponent,
    KfmContactRegistrationsComponent,
  ],
  templateUrl: './kfm-contact-create-edit-dialog.component.html',
  host: { class: 'kfm-contact-create-edit-dialog layer-one', style: 'display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;' },
})
export class KfmContactCreateEditDialogComponent {
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _mapper = new ContactMapper();

  protected readonly dialogRef = inject<DialogRef<Contact>>(DialogRef<Contact>);

  private readonly _data = inject<{
    contact: HsContact | null;
    bankAccount: BankAccount | null;
    registrations: Registration[] | null;
  }>(DIALOG_DATA);

  protected readonly addressTabLabel = ADDRESS_TAB_LABEL;
  protected readonly bankTabLabel = BANK_TAB_LABEL;
  protected readonly idTabLabel = ID_TAB_LABEL;
  protected readonly okButtonLabel = SAVE_LABEL;
  protected readonly cancelButtonLabel = CANCEL_LABEL;

  protected selectedContact!: HsContact;
  protected registrations!: Registration[];
  protected bankAccount!: BankAccount;

  protected readonly hasDuplicateCustomerNumber = signal(false);

  constructor() {
    this.configureContact();
  }

  protected get canSave(): boolean {
    if (this.hasDuplicateCustomerNumber()) return false;

    const contact = this.selectedContact;

    if (contact) {
      if (contact.contactType === ContactType.Company) {
        return contact.companyName?.trim().length > 0;
      } else {
        return (
          contact.firstName.trim().length > 0 ||
          contact.lastName.trim().length > 0
        );
      }
    }

    return false;
  }

  protected onOkClicked() {
    const result = this.selectedContact;
    const contact = this._mapper.mapToContact(result);

    // remove all bank accounts that have an empty guid
    if (contact.bankAccounts) {
      contact.bankAccounts = contact.bankAccounts.filter(
        (ba) => ba.uId !== this._guidGenerator.emptyUUID,
      );

      contact.bankAccounts.push(this.bankAccount);
    } else {
      contact.bankAccounts = [this.bankAccount];
    }

    // remove all registrations that have an empty guid
    if (contact.registrations) {
      contact.registrations = contact.registrations.filter(
        (reg) => reg.uId !== this._guidGenerator.emptyUUID,
      );

      contact.registrations.push(...this.registrations);
    } else {
      contact.registrations = this.registrations;
    }

    this.dialogRef.close(contact);
  }

  private configureContact() {
    if (this._data && this._data.contact) {
      // Create a copy to avoid mutating the input data directly
      this.selectedContact = { ...this._data.contact };
    } else {
      this.selectedContact = {
        uId: this._guidGenerator.emptyUUID,
        firstName: '',
        lastName: '',
        contactType: ContactType.Person,
        title: '',
        salutation: '',
        street: '',
        houseNumber: '',
        genderType: HsGenderType.Male,
        postalCode: '',
        city: '',
        emailAddress: '',
        faxNumber: '',
        telephoneNumber: '',
        mobileTelephoneNumber: '',
        homePage: '',
        district: '',
        countryType: Iso3166CountryType.DE,
      };
    }

    this.bankAccount =
      this._data && this._data.bankAccount
        ? { ...this._data.bankAccount }
        : {
            uId: this._guidGenerator.emptyUUID,
            tenantUId: this._guidGenerator.emptyUUID,
            userUId: this._guidGenerator.emptyUUID,
            stampCreate: new Date().toISOString(),
            syncType: 0,
            concurrencyIdentificator: this._guidGenerator.emptyUUID,
            concurrencyStamp: new Date().toISOString(),
            contactUId: this.selectedContact
              ? this.selectedContact.uId
              : this._guidGenerator.emptyUUID,
          };

    this.registrations =
      this._data && this._data.registrations
        ? this._data.registrations.map((reg) => ({ ...reg }))
        : [];
  }
}
